<template>
    <modal @close="$emit('close')" :loading="loading">
        <template slot="header">
            {{ isEditing ? 'Edit Campaign Template' : 'Add Campaign Template' }}
            <button type="button" class="btn btn-primary" @click="save" :disabled="!canSave">Save</button>
            <button class="btn btn-link" @click="$emit('close')">Cancel</button>
        </template>

        <div class="alert alert-danger" v-if="err">{{ err }}</div>
        <div class="alert alert-success" v-if="success">{{ success }}</div>

        <div class="row">
            <div class="col-6">
                <fieldset class="mb-3" :class="{ 'loading-mask': loading }">
                    <div class="form-group row">
                        <label class="col-form-label col-lg-4">Template name</label>
                        <div class="col-lg-8">
                            <input type="text" class="form-control" v-model="form.name"
                                placeholder="Enter template name">
                        </div>
                    </div>

                    <div class="form-group row">
                        <label class="col-form-label col-lg-4">Sender template</label>
                        <div class="col-lg-8">
                            <multiselect v-model="form.sender_template" tag-placeholder=""
                                placeholder="Search sender templates" label="_id" track-by="_id"
                                :options="senderTemplateOptions || []" :multiple="false">
                            </multiselect>
                        </div>
                    </div>

                    <div class="form-group row">
                        <label class="col-form-label col-lg-4">Time interval (minutes)</label>
                        <div class="col-lg-8">
                            <input type="number" min="0" class="form-control" v-model="form.time_interval"
                                placeholder="0">
                            <small>Time interval between campaigns in minutes.</small>
                        </div>
                    </div>

                    <div class="form-group row">
                        <label class="col-form-label col-lg-4">Start hours</label>
                        <div class="col-lg-8">
                            <input type="text" class="form-control" v-model="form.start_hours"
                                placeholder="5:20 or 5.20">
                            <small>Time when campaigns should start. Examples: 5:20 = 05:20AM, 22:30 = 10:30PM, 5.20 =
                                05:20AM</small>
                        </div>
                    </div>

                    <div class="form-group row">
                        <label class="col-form-label col-lg-4">Message template</label>
                        <div class="col-lg-8">
                            <multiselect v-model="form.message_template" tag-placeholder=""
                                placeholder="Search message template" label="name" track-by="_id"
                                :options="messageTemplateOptions || []" :multiple="false">
                            </multiselect>
                        </div>
                    </div>
                </fieldset>
            </div>

            <div class="col-6">
                <div class="form-group row">
                    <div class="col-lg-12">
                        <label class="col-form-label col-lg-12">Domains (one per line)</label>
                        <ace-editor :editor-id="'template_domains'" @change-content="contentChangedDomains"
                            :content="form.domains_text || ''" lang="text" theme="monokai"
                            v-bind:style="{ 'width': '100%', height: '200px' }">
                        </ace-editor>
                        <small>Enter domain names, one per line (e.g., example.com, sub.example.com)</small>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-6">
                <div class="form-group row">
                    <div class="col-lg-12">
                        <label class="col-form-label col-lg-12">Contact lists (one per line)</label>
                        <ace-editor :editor-id="'template_contact_lists'" @change-content="contentChangedContactLists"
                            :content="form.contact_lists_text || ''" lang="text" theme="monokai"
                            v-bind:style="{ 'width': '100%', height: '200px' }">
                        </ace-editor>
                        <small>Enter contact list names, one per line</small>
                    </div>
                </div>
            </div>

            <div class="col-6">
                <div class="form-group row">
                    <div class="col-lg-12">
                        <label class="col-form-label col-lg-12">Subjects (one per line)</label>
                        <ace-editor :editor-id="'template_subjects'" @change-content="contentChangedSubjects"
                            :content="form.subjects_text || ''" lang="text" theme="monokai"
                            v-bind:style="{ 'width': '100%', height: '200px' }">
                        </ace-editor>
                        <small>Enter email subjects, one per line</small>
                    </div>
                </div>
            </div>
        </div>

        <div class="row" v-if="validationErrors">
            <div class="col-lg-12">
                <div class="alert alert-danger" v-html="validationErrors"></div>
            </div>
        </div>
    </modal>
</template>

<script>
import modal from '../common/Modal.vue'
import aceEditor from '../common/Ace.js'

export default {
    components: { modal, aceEditor },
    props: {
        id: {
            type: [String, Boolean],
            default: false
        }
    },
    data() {
        return {
            form: {
                name: '',
                sender_template: '',
                time_interval: 0,
                start_hours: '0',
                message_template: null,
                domains_text: '',
                contact_lists_text: '',
                subjects_text: ''
            },

            // Options for dropdowns
            senderTemplateOptions: [],
            messageTemplateOptions: [],

            // Validation
            validationErrors: '',

            // UI state
            loading: false,
            err: null,
            success: null
        }
    },
    computed: {
        isEditing() {
            return this.id && this.id !== true && !this.id.toString().startsWith('clone_')
        },
        isCloning() {
            return this.id && this.id.toString().startsWith('clone_')
        },
        actualTemplateId() {
            if (this.isCloning) {
                return this.id.toString().replace('clone_', '')
            }
            return this.id
        },
        canSave() {
            return this.form.name && this.form.name.trim().length > 0
        },
        parsedDomains() {
            return this.form.domains_text.split('\n').map(s => s.trim()).filter(s => s)
        },
        parsedContactLists() {
            return this.form.contact_lists_text.split('\n').map(s => s.trim()).filter(s => s)
        }
    },
    methods: {
        contentChangedDomains(content) {
            this.form.domains_text = content
        },
        contentChangedContactLists(content) {
            this.form.contact_lists_text = content
        },
        contentChangedSubjects(content) {
            this.form.subjects_text = content
        },

        async validateInputs() {
            this.validationErrors = ''

            if (!this.form.name || this.form.name.trim().length === 0) {
                this.validationErrors = 'Template name is required'
                return false
            }

            // Parse domains and contact lists for validation (preserve duplicates like MultipleCampaignCreator)
            let domains = this.parsedDomains
            let contactLists = this.parsedContactLists

            if (domains.length > 0 || contactLists.length > 0) {
                try {
                    let verifyData = {
                        domains: domains,
                        contact_lists: contactLists
                    }

                    let response = await this.$api.postReq('verify', verifyData)
                    let result = response.body.result

                    let errors = []
                    if (result.missing_domains && result.missing_domains.length > 0) {
                        errors.push(`Missing or invalid domains: ${result.missing_domains.join(', ')}`)
                    }
                    if (result.missing_lists && result.missing_lists.length > 0) {
                        errors.push(`Missing contact lists: ${result.missing_lists.join(', ')}`)
                    }

                    if (errors.length > 0) {
                        this.validationErrors = errors.join('<br/>')
                        return false
                    }
                } catch (e) {
                    this.validationErrors = 'Error validating domains and contact lists'
                    return false
                }
            }

            return true
        },

        async save() {
            this.err = null
            this.success = null

            // Validate inputs
            if (!(await this.validateInputs())) {
                return
            }

            this.loading = true

            try {
                let payload = {
                    name: this.form.name,
                    sender_template: this.form.sender_template,
                    time_interval: parseInt(this.form.time_interval) || 0,
                    start_hours: this.form.start_hours || '0',
                    message_template: this.form.message_template ? this.form.message_template._id : null,
                    domains_text: this.form.domains_text,
                    contact_lists_text: this.form.contact_lists_text,
                    subjects_text: this.form.subjects_text
                }

                if (this.isEditing) {
                    payload._id = this.id
                }

                let response = await this.$api.postReq('campaign_template', payload)

                this.success = 'Campaign template saved successfully'
                setTimeout(() => {
                    this.$emit('close')
                    this.$emit('reload')
                }, 1000)

            } catch (e) {
                console.error(e)
                this.err = e.body && e.body.error ? e.body.error : 'Error saving campaign template'
            } finally {
                this.loading = false
            }
        },

        async loadTemplate() {
            if (!this.isEditing && !this.isCloning) return

            this.loading = true
            try {
                let response = await this.$api.getReq(`campaign_template/${this.actualTemplateId}`)
                let data = response.body.data

                this.form = {
                    name: data.name || '',
                    sender_template: data.sender_template || [],
                    time_interval: data.time_interval || 0,
                    start_hours: data.start_hours || '0',
                    message_template: data.message_template || null,
                    domains_text: data.domains_text || '',
                    contact_lists_text: data.contact_lists_text || '',
                    subjects_text: data.subjects_text || ''
                }

            } catch (e) {
                this.err = 'Error loading campaign template'
            } finally {
                this.loading = false
            }
        },

        async loadOptions() {
            try {
                // Load sender templates (name templates)
                let response = await this.$api.getReq('campaign_create')
                let data = response.body.data

                this.senderTemplateOptions = data.name_templates || []
                this.messageTemplateOptions = data.message_templates || []

            } catch (e) {
                console.error('Error loading options:', e)
            }
        }
    },

    async mounted() {
        await this.loadOptions()

        // Fill with test data when running on localhost
        if (document.location.href.indexOf('localhost') >= 0) {
            this.form.contact_lists_text = `foo
foo`
            this.form.domains_text = `a1.baz.com
a1.baz.com`
            this.form.subjects_text = `test
test2`
            this.form.name = 'test template'
            this.form.time_interval = 10
        }

        if (this.isEditing || this.isCloning) {
            await this.loadTemplate()
        }
    }
}
</script>

<style scoped>
.loading-mask {
    position: relative;
    pointer-events: none;
    opacity: 0.6;
}

.loading-mask::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 1000;
}
</style>



// WEBPACK FOOTER //
// src/components/modal/CampaignTemplate.vue